(() => {
  'use strict';
  const host = document.querySelector('solebrace-cart');
  if (!host || host.shadowRoot || window.SoleBraceCart) return;
  const markup = host.querySelector('template[data-sbc-markup]');
  if (!markup) return;
  const scope = host.attachShadow({ mode: 'open' });
  scope.appendChild(markup.content.cloneNode(true));
  markup.remove();
  const config = JSON.parse(scope.querySelector('#sbc-config').textContent);
  const stylesReady = Promise.all([...scope.querySelectorAll('link[rel="stylesheet"]')].map(link =>
    link.sheet ? Promise.resolve() : new Promise((resolve, reject) => {
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', () => reject(new Error('Cart styles could not load. Please refresh.')), { once: true });
    })
  ));
  const rootPath = config.root.endsWith('/') ? config.root : config.root + '/';
  const root = scope.querySelector('#sbCart');
  const guarantee = document.createElement('div');
  guarantee.className = 'sbcart-guarantee';
  guarantee.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M12 2.5 20 6v5.5c0 5-3.3 8.8-8 10-4.7-1.2-8-5-8-10V6l8-3.5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="m8.5 12 2.3 2.3 4.7-4.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg><span>30 Day Guarantee • Try Them Risk Free</span>';
  root.querySelector('.sbcart-public-trust-badges').before(guarantee);
  const popup = root.querySelector('#SbcartPopup');
  const panel = root.querySelector('#SBCartPopup');
  const rowTemplate = scope.querySelector('#sbc-row-template');
  const itemsList = root.querySelector('[data-sbc-items]');
  const errorBox = scope.querySelector('#sbc-error');
  const productCache = new Map();
  const workfitIds = new Set([7768760647889, 7770368868561, 7806401773777, 7808438698193]);
  const addonHandle = 'rollpro™-foot-massager';
  let cart = null;
  let busy = false;
  let opener = null;
  let closeTimer = null;
  let addonProduct = null;
  let version = 0;
  const exchangeRate = Number(window.Shopify?.currency?.rate) || 1;
  const $ = (selector, parent = root) => parent.querySelector(selector);
  const cls = name => `sbcart-internal-component-product-tile__${name} sbcart-public-component-product-tile__${name}`;
  const money = amount => {
    const currency = cart?.currency || config.currency;
    const symbol = new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).formatToParts(0).find(p => p.type === 'currency')?.value || currency;
    return `${symbol}${(amount / 100).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  };
  async function request(path, body) {
    const response = await fetch(rootPath + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Accept': 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || `Cart request failed (${response.status}).`);
    return data;
  }
  function product(handle) {
    if (!productCache.has(handle)) productCache.set(handle, request('products/' + encodeURIComponent(handle) + '.js').catch(error => { productCache.delete(handle); throw error; }));
    return productCache.get(handle);
  }
  function sizeSystem() {
    if (location.hostname.startsWith('us.')) return 'US';
    return config.country === 'AU' ? 'AU' : config.country === 'GB' ? 'UK' : 'US';
  }
  function sizeChoices(p, selectedId) {
    const system = sizeSystem();
    const options = p.options || [];
    const name = o => typeof o === 'string' ? o : o.name;
    const visibleIndex = options.findIndex(o => name(o) === system + ' Shoe Size');
    const otherIndex = options.findIndex(o => name(o) === (system === 'AU' ? 'US' : 'AU') + ' Shoe Size');
    const optionValue = (v, i) => v.options?.[i] || v['option' + (i + 1)] || '';
    const values = i => options[i]?.values || [...new Set(p.variants.map(v => optionValue(v, i)))];
    const selected = p.variants.find(v => Number(v.id) === Number(selectedId));
    let choices;
    if (visibleIndex >= 0 && otherIndex >= 0) {
      const otherValues = values(otherIndex);
      choices = values(visibleIndex).map((label, i) => {
        const variant = selected && optionValue(selected, visibleIndex) === label ? selected : p.variants.find(v => optionValue(v, visibleIndex) === label && optionValue(v, otherIndex) === otherValues[i]);
        return variant ? { variant, label } : null;
      }).filter(Boolean);
    } else {
      choices = p.variants.map(variant => ({ variant, label: variant.title }));
    }
    return choices.filter(c => c.variant.available || Number(c.variant.id) === Number(selectedId));
  }
  function setBusy(value) {
    busy = value;
    root.setAttribute('aria-busy', String(value));
    root.querySelectorAll('button:not([data-sbc-close]),select,input').forEach(e => { e.disabled = value; });
    updateAddon();
  }
  function error(message) {
    errorBox.textContent = message;
    errorBox.hidden = !message;
  }
  async function refresh() {
    const ticket = ++version;
    const latest = await request('cart.js');
    const products = await Promise.allSettled([...new Set(latest.items.map(i => i.handle))].map(product));
    if (ticket !== version) return;
    cart = latest;
    const byId = new Map(products.filter(p => p.status === 'fulfilled').map(p => [Number(p.value.id), p.value]));
    render(byId);
    updateHeaderCounts();
  }
  function appendPrice(parent, name, text) {
    const span = document.createElement('span');
    span.className = cls(name);
    span.textContent = text;
    parent.appendChild(span);
  }
  function render(products) {
    root.querySelectorAll('.sbcart-public-cart-item,.sbc-empty,.sbc-loading').forEach(e => e.remove());
    const fragment = document.createDocumentFragment();
    // Shopify may reverse discounted duplicate rows after a cart mutation.
    // Keep each variant's existing slots, with its paid lines before free lines.
    const variants = new Map();
    for (const item of cart.items) {
      if (!variants.has(item.variant_id)) variants.set(item.variant_id, []);
      variants.get(item.variant_id).push(item);
    }
    for (const lines of variants.values()) lines.sort((a, b) => Number(a.final_line_price === 0) - Number(b.final_line_price === 0));
    const displayItems = cart.items.map(item => variants.get(item.variant_id).shift());
    for (const item of displayItems) {
      const row = rowTemplate.content.firstElementChild.cloneNode(true);
      row.dataset.key = item.key;
      row.dataset.variantId = item.variant_id;
      const title = $('.sbcart-public-component-product-tile__title-link', row);
      title.textContent = item.product_title;
      title.href = item.url;
      $('.sbcart-public-component-product-tile__image-wrapper', row).href = item.url;
      const img = $('img', row);
      img.src = item.featured_image?.url || item.image || '';
      img.alt = item.product_title;
      if (!img.getAttribute('src')) img.remove();
      const variantRow = $('.sbcart-public-component-product-tile__variant', row);
      const p = products.get(Number(item.product_id));
      if (workfitIds.has(Number(item.product_id)) && p) {
        const select = $('select', variantRow);
        select.setAttribute('aria-label', sizeSystem() + ' shoe size');
        for (const choice of sizeChoices(p, item.variant_id)) select.add(new Option(choice.label, choice.variant.id, false, Number(choice.variant.id) === Number(item.variant_id)));
        select.addEventListener('change', () => mutate(() => replaceVariant(item.key, Number(select.value)), false));
      } else {
        variantRow.textContent = item.variant_title === 'Default Title' ? '' : item.variant_title || '';
      }
      const properties = $('.sbcart-public-component-product-tile__properties', row);
      properties.replaceChildren();
      for (const [key, value] of Object.entries(item.properties || {})) {
        if (key.startsWith('_') || value === '' || value === null) continue;
        const line = document.createElement('div'); line.textContent = `${key}: ${value}`; properties.appendChild(line);
      }
      if (item.selling_plan_allocation?.selling_plan?.name) {
        const line = document.createElement('div'); line.textContent = item.selling_plan_allocation.selling_plan.name; properties.appendChild(line);
      }
      const pricing = $('.sbcart-public-component-product-tile__product-pricing', row);
      pricing.replaceChildren();
      const massagerCompareAt = Number(item.product_id) === 7642284032209
        ? Number(p?.variants?.find(v => Number(v.id) === Number(item.variant_id))?.compare_at_price || 0) * item.quantity
        : 0;
      const compareAt = Math.max(item.original_line_price, massagerCompareAt);
      const savings = compareAt - item.final_line_price;
      if (savings > 0) appendPrice(pricing, 'price-compare-at', money(compareAt));
      appendPrice(pricing, 'price', money(item.final_line_price));
      if (savings > 0) appendPrice(pricing, 'savings', `(Save ${money(savings)})`);
      const quantity = $('input', row);
      quantity.value = item.quantity;
      quantity.addEventListener('change', () => {
        if (!/^\d+$/.test(quantity.value)) { quantity.value = item.quantity; return; }
        mutate(() => changeQuantity(item.key, Number(quantity.value)));
      });
      $('.sbcart-public-component-product-tile__quantity-minus', row).addEventListener('click', () => mutate(() => changeQuantity(item.key, item.quantity - 1)));
      $('.sbcart-public-component-product-tile__quantity-plus', row).addEventListener('click', () => mutate(() => changeQuantity(item.key, item.quantity + 1)));
      const remove = $('.sbcart-public-component-product-tile__remove-button', row);
      remove.setAttribute('aria-label', `Remove ${item.product_title}`);
      remove.addEventListener('click', () => mutate(() => changeQuantity(item.key, 0)));
      quantity.addEventListener('keydown', event => {
        if (!['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Enter' ? Number(quantity.value) : item.quantity + (event.key === 'ArrowUp' ? 1 : -1);
        if (Number.isSafeInteger(next) && next >= 0) mutate(() => changeQuantity(item.key, next));
      });
      fragment.appendChild(row);
    }
    if (!cart.items.length) {
      const empty = document.createElement('li'); empty.className = 'sbc-empty';
      empty.innerHTML = '<h2>Your cart is empty</h2><button type="button" data-sbc-close>Continue shopping</button>';
      fragment.appendChild(empty);
    }
    itemsList.appendChild(fragment);
    $('[data-sbc-count]').textContent = `${cart.item_count} Items`;
    $('[data-sbc-checkout] span').textContent = 'Secure Checkout • ' + money(cart.total_price);
    $('.sbcart-footer').hidden = !cart.items.length;
    const threshold = Math.round(10001 * exchangeRate);
    const complete = cart.total_price >= threshold;
    const rewards = $('[data-sbc-rewards]');
    rewards.innerHTML = complete ? '<p><strong>You\'ve unlocked FREE Shipping! 🚚</strong></p>' : '<p>You’re <span></span> From <strong>FREE</strong> Shipping!</p>';
    if (!complete) $('span', rewards).textContent = money(threshold - cart.total_price);
    $('.sbcart-public-component-segmented-progress-bar__segment-fill').style.width = `${Math.min(100, cart.total_price / threshold * 100)}%`;
    $('.sbcart-public-component-segmented-progress-bar__milestone-icon').dataset.internalProperties = `isCompleted=${complete}`;
    setBusy(busy);
  }
  function updateAddon() {
    if (!addonProduct) return;
    const variant = addonProduct.variants.find(v => v.available) || addonProduct.variants[0];
    $('[data-sbc-addon-price]').textContent = money(variant.price);
    const selected = cart?.items.some(i => Number(i.product_id) === Number(addonProduct.id));
    const input = $('[data-sbc-addon] input');
    input.checked = !!selected;
    input.setAttribute('aria-checked', String(!!selected));
    input.disabled = busy || (!variant.available && !selected);
  }
  async function currentLine(key) {
    const latest = await request('cart.js');
    const item = latest.items.find(i => i.key === key);
    if (!item) throw new Error('Your cart changed in another tab. It has been refreshed; please try again.');
    return item;
  }
  async function changeQuantity(key, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Please enter a valid quantity.');
    if (quantity === 0) {
      // One keyed update removes the selected row without the extra read and
      // slower change endpoint. Verify the exact variant/property quantity so
      // a stale discount key cannot silently leave the row in the cart.
      const selected = cart?.items.find(i => i.key === key);
      if (!selected) throw new Error('Your cart changed in another tab. It has been refreshed; please try again.');
      const id = identity(selected);
      const expected = cart.items.filter(i => identity(i) === id).reduce((sum, i) => sum + i.quantity, 0) - selected.quantity;
      const updated = await request('cart/update.js', { updates: { [key]: 0 } });
      const actual = updated.items.filter(i => identity(i) === id).reduce((sum, i) => sum + i.quantity, 0);
      if (actual !== expected) throw new Error('This item changed before it could be removed. The cart has been refreshed; please try again.');
      return updated;
    }
    const item = await currentLine(key);
    return request('cart/change.js', { id: item.key, quantity });
  }
  const identity = item => JSON.stringify([Number(item.variant_id || item.id), Object.entries(item.properties || {}).sort(), item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null]);
  async function replaceVariant(key, variantId) {
    const before = await request('cart.js');
    const item = before.items.find(i => i.key === key);
    if (!item) throw new Error('Your cart changed in another tab. It has been refreshed; please try again.');
    if (variantId === Number(item.variant_id)) return;
    const p = await product(item.handle);
    if (!sizeChoices(p, item.variant_id).some(c => Number(c.variant.id) === variantId && c.variant.available)) throw new Error('This size is unavailable.');
    const added = { id: variantId, quantity: item.quantity, properties: item.properties || {} };
    const plan = item.selling_plan_allocation?.selling_plan?.id;
    if (plan) added.selling_plan = plan;
    const oldIdentity = identity(item);
    const newIdentity = identity(added);
    const count = (items, id) => items.filter(i => identity(i) === id).reduce((sum, i) => sum + i.quantity, 0);
    // Rapi can add a *different* WorkFit size while recalculating a bundle.
    // Reconcile every size of this product, not only the two sizes we swapped.
    const expected = new Map();
    const originals = new Map();
    for (const line of before.items.filter(line => Number(line.product_id) === Number(item.product_id))) {
      const id = identity(line);
      expected.set(id, (expected.get(id) || 0) + line.quantity);
      originals.set(id, line);
    }
    expected.set(oldIdentity, expected.get(oldIdentity) - item.quantity);
    expected.set(newIdentity, (expected.get(newIdentity) || 0) + item.quantity);
    originals.set(newIdentity, added);
    // Updating two keyed quantities at once avoids the remove/add cycle when
    // the destination already has the same bundle properties.
    const destination = before.items.find(line => identity(line) === newIdentity);
    const destinationVariantExists = before.items.some(line => Number(line.variant_id) === variantId);
    if (!plan && destination) {
      await request('cart/update.js', { updates: { [item.key]: 0, [destination.key]: destination.quantity + item.quantity } });
    } else if (!plan && !destinationVariantExists) {
      // Ajax update adds a new variant without line properties. Restore the
      // offer properties on its discounted sub-lines before reconciliation.
      await request('cart/update.js', { updates: { [item.key]: 0, [variantId]: item.quantity } });
      if (Object.keys(item.properties || {}).length) {
        for (let attempt = 0; attempt < 6; attempt++) {
          const latest = await request('cart.js');
          const incomplete = latest.items.find(line => Number(line.variant_id) === variantId && identity(line) !== newIdentity);
          if (!incomplete) break;
          await request('cart/change.js', { id: incomplete.key, properties: item.properties });
        }
      }
    } else {
      const removed = await request('cart/change.js', { id: item.key, quantity: 0 });
      const baseline = count(removed.items, newIdentity);
      try { await request('cart/add.js', { items: [added] }); }
      catch (failure) {
        // A lost response can follow a successful add. Check before compensating.
        let check;
        try { check = await request('cart.js'); }
        catch (_) { throw new Error('Connection interrupted during the size change. Refresh your cart before retrying.'); }
        const now = count(check.items, newIdentity);
        if (now < baseline + item.quantity) {
          if (now !== baseline) throw new Error('The cart changed during the size update. Please review its quantities before continuing.');
          try { await request('cart/add.js', { items: [{ ...added, id: item.variant_id }] }); }
          catch (_) { throw new Error('The new size could not be added and the original could not be restored. Please add the original item again before checkout.'); }
          throw new Error(failure.message + ' Your original size was restored.');
        }
      }
    }
    // Bundle discounts can split a variant into paid and free lines, and Rapi
    // can insert an unrelated size during the add. Preserve the whole product.
    for (let attempt = 0; attempt < 12; attempt++) {
      const latest = await request('cart.js');
      const lines = latest.items.filter(line => Number(line.product_id) === Number(item.product_id));
      const actual = new Map();
      for (const line of lines) actual.set(identity(line), (actual.get(identity(line)) || 0) + line.quantity);
      const mismatch = [...new Set([...expected.keys(), ...actual.keys()])]
        .map(id => ({ id, wanted: expected.get(id) || 0, found: actual.get(id) || 0 }))
        .filter(entry => entry.wanted !== entry.found)
        .sort((a, b) => (b.found > b.wanted) - (a.found > a.wanted))[0];
      if (!mismatch) {
        // Promotions may change other gift products; those are not cart lines
        // for this WorkFit product and should not be rewritten here.
        return;
      }
      if (mismatch.found > mismatch.wanted) {
        const line = lines.find(i => identity(i) === mismatch.id);
        await request('cart/change.js', { id: line.key, quantity: Math.max(0, line.quantity - (mismatch.found - mismatch.wanted)) });
      } else {
        const original = originals.get(mismatch.id);
        if (!original) throw new Error('The cart changed during the size update. Please review its quantities before checkout.');
        const originalPlan = original.selling_plan_allocation?.selling_plan?.id || original.selling_plan;
        await request('cart/add.js', { items: [{ id: original.variant_id || original.id, quantity: mismatch.wanted - mismatch.found, properties: original.properties || {}, ...(originalPlan ? { selling_plan: originalPlan } : {}) }] });
      }
    }
    throw new Error('The size update could not preserve the original pair count. Please review your cart before checkout.');
  }
  async function mutate(action, notify = true) {
    if (busy) return;
    error(''); setBusy(true);
    try { await action(); }
    catch (failure) { error(failure.message); }
    finally {
      try { await refresh(); } catch (failure) { error(failure.message); }
      setBusy(false);
      if (notify) document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'solebrace-cart' } }));
    }
  }
  function show(refreshCart = true) {
    window.clearTimeout(closeTimer);
    if (popup.dataset.open === 'true') {
      if (refreshCart) refresh().catch(failure => error(failure.message));
      return;
    }
    if (popup.hidden) {
      opener = document.activeElement;
      popup.hidden = false;
      // Commit the offscreen position before transitioning into the viewport.
      panel.getBoundingClientRect();
    }
    popup.dataset.open = 'true';
    document.body.classList.add('sbc-open');
    $('[data-sbc-close]').focus({ preventScroll: true });
    if (refreshCart) refresh().catch(failure => error(failure.message));
  }
  function finishHide() {
    if (popup.dataset.open === 'true' || popup.hidden) return;
    window.clearTimeout(closeTimer);
    popup.hidden = true;
    document.body.classList.remove('sbc-open');
    opener?.focus?.({ preventScroll: true });
  }
  function hide() {
    if (popup.hidden || popup.dataset.open !== 'true') return;
    popup.dataset.open = 'false';
    error('');
    window.clearTimeout(closeTimer);
    // Keep the drawer mounted until its exit finishes. The fallback also
    // handles interrupted transitions and reduced-motion preferences.
    closeTimer = window.setTimeout(finishHide, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 360);
  }
  panel.addEventListener('transitionend', event => {
    if (event.target === panel && event.propertyName === 'transform') finishHide();
  });
  scope.addEventListener('click', event => {
    if (event.target.closest('[data-sbc-open]')) show();
    if (event.target.closest('[data-sbc-close]')) hide();
    if (event.target.closest('[data-sbc-refresh]')) refresh().catch(failure => error(failure.message));
  });
  scope.addEventListener('keydown', event => {
    if (popup.hidden) return;
    if (event.key === 'Escape') { hide(); return; }
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex="0"]')].filter(e => e.getClientRects().length);
    const first = focusable[0], last = focusable.at(-1);
    if (event.shiftKey && scope.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && scope.activeElement === last) { event.preventDefault(); first?.focus(); }
  });
  $('[data-sbc-addon] input').addEventListener('change', event => {
    const checked = event.target.checked;
    mutate(async () => {
      const latest = await request('cart.js');
      const lines = latest.items.filter(i => Number(i.product_id) === Number(addonProduct.id));
      if (checked && !lines.length) {
        const variant = addonProduct.variants.find(v => v.available);
        if (!variant) throw new Error('Foot Massager is currently unavailable.');
        await request('cart/add.js', { items: [{ id: variant.id, quantity: 1 }] });
      } else if (!checked) {
        for (const line of lines) {
          const current = (await request('cart.js')).items.find(i => identity(i) === identity(line));
          if (current) await request('cart/change.js', { id: current.key, quantity: 0 });
        }
      }
    });
  });
  scope.querySelector('#sbc-checkout').addEventListener('submit', event => {
    if (busy || !cart?.item_count) event.preventDefault();
  });

  function prefersUpcart() {
    return config.upcartEnabled !== false && config.upcartEnabled !== 'false'
      && typeof window.upcartOpenCart === 'function';
  }
  function updateHeaderCounts() {
    if (!cart) return;
    const icon = document.querySelector('#cart-icon-bubble');
    if (icon && !icon.querySelector('.cart-count-bubble') && cart.item_count) {
      const badge = document.createElement('div');
      badge.className = 'cart-count-bubble';
      const number = document.createElement('span');
      number.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      badge.append(number, label); icon.appendChild(badge);
    }
    for (const badge of document.querySelectorAll('.cart-count-bubble, .sb10r-utility-header__cart-count, [data-cart-count]')) {
      if (badge.closest('#upCart, #UpcartPopup, cart-drawer')) continue;
      badge.hidden = !cart.item_count;
      const number = badge.querySelector('[aria-hidden="true"]');
      if (number) number.textContent = cart.item_count < 100 ? String(cart.item_count) : '99+';
      else badge.textContent = String(cart.item_count);
      const label = badge.querySelector('.visually-hidden');
      if (label) label.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    }
  }
  async function routeOpen(trigger) {
    if (prefersUpcart()) { window.upcartOpenCart(); return; }
    try {
      await stylesReady;
      if (trigger instanceof HTMLElement && !scope.contains(trigger)) trigger.focus({ preventScroll: true });
      show();
    } catch (_) {
      location.assign(config.cartUrl);
    }
  }
  function bindNativeCarts() {
    for (const node of document.querySelectorAll('cart-drawer, cart-notification')) {
      if (node.dataset.sbcAdapted) continue;
      node.dataset.sbcAdapted = 'true';
      node.open = trigger => routeOpen(trigger || node.activeElement);
      node.close = hide;
      node.renderContents = () => routeOpen(node.activeElement);
      // Keep getSectionsToRender/setActiveElement intact for Shopify product forms.
    }
  }
  const cartTriggerSelector = '#cart-icon-bubble,.header__icon--cart,[data-cart-toggle],[data-cart-open],[aria-controls="CartDrawer"],button[aria-label="Cart"],button[aria-label="Open cart"],a[href]';
  function cartTrigger(event) {
    const node = event.target.closest?.(cartTriggerSelector);
    if (!node || node.closest('solebrace-cart,#upCart,#UpcartPopup')) return null;
    if (node.tagName === 'A') {
      if (node.target === '_blank' || node.hasAttribute('download')) return null;
      try {
        const url = new URL(node.href, location.href);
        if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== config.cartUrl.replace(/\/$/, '') || url.search) return null;
      } catch (_) { return null; }
    }
    return node;
  }
  document.addEventListener('click', event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || prefersUpcart()) return;
    const node = cartTrigger(event);
    if (!node) return;
    event.preventDefault(); event.stopImmediatePropagation();
    routeOpen(node);
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== ' ' || prefersUpcart()) return;
    const node = cartTrigger(event);
    if (!node) return;
    event.preventDefault(); event.stopImmediatePropagation(); routeOpen(node);
  }, true);

  let syncTimer;
  let openAfterSync = false;
  let syncing = false;
  let syncAgain = false;
  const signature = () => cart ? JSON.stringify([cart.currency, cart.total_price, cart.items.map(i => [i.key, i.quantity, i.final_line_price])]) : null;
  function scheduleSync(openAfter = false) {
    if (prefersUpcart() || busy) return;
    openAfterSync = openAfterSync || openAfter;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncFromStore, 180);
  }
  async function syncFromStore() {
    if (prefersUpcart() || busy) return;
    if (syncing) { syncAgain = true; return; }
    syncing = true;
    const shouldOpen = openAfterSync; openAfterSync = false;
    const before = signature();
    try {
      await refresh();
      if (shouldOpen && !prefersUpcart() && cart.item_count && before !== signature()) {
        await stylesReady; show(false);
      }
    } catch (failure) {
      if (!popup.hidden) error(failure.message);
    } finally {
      syncing = false;
      if (syncAgain) { syncAgain = false; scheduleSync(); }
    }
  }
  if ('PerformanceObserver' in window) {
    new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        let url; try { url = new URL(entry.name); } catch (_) { continue; }
        if (url.origin !== location.origin || entry.responseStatus >= 400) continue;
        const match = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/);
        if (match) scheduleSync(match[1] === 'add');
      }
    }).observe({ type: 'resource', buffered: false });
  }
  for (const type of ['cart:updated', 'cart:change', 'cart:refresh', 'cart:updated:ajax']) {
    document.addEventListener(type, event => {
      if (event.detail?.source !== 'solebrace-cart') scheduleSync();
    });
  }
  document.addEventListener('cart:open', () => routeOpen());
  // Plain Shopify forms have no product-form controller. Let Rapi's form
  // handlers prepare their full bundle payload before replacing navigation.
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (event.defaultPrevented || prefersUpcart() || !(form instanceof HTMLFormElement)) return;
    const url = new URL(form.action, location.href);
    if (url.origin !== location.origin || !/\/cart\/add(?:\.js)?$/.test(url.pathname)) return;
    event.preventDefault();
    if (form.dataset.sbcSubmitting) return;
    form.dataset.sbcSubmitting = 'true';
    const button = event.submitter;
    const wasDisabled = button?.disabled;
    const body = new FormData(form);
    if (button) button.disabled = true;
    try {
      const response = await fetch(rootPath + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Unable to add this item.');
      await routeOpen(button);
    } catch (failure) {
      await routeOpen(button);
      error(failure.message);
    } finally {
      delete form.dataset.sbcSubmitting;
      if (button) button.disabled = wasDisabled;
    }
  });
  document.addEventListener('shopify:section:load', () => { bindNativeCarts(); scheduleSync(); });
  window.addEventListener('pageshow', () => scheduleSync());
  window.addEventListener('focus', () => scheduleSync());
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleSync(); });

  window.SoleBraceCart = { open: routeOpen, close: hide, refresh, prefersUpcart, version: '1.0.0' };
  for (const name of ['cart-drawer', 'cart-notification']) {
    customElements.whenDefined(name).then(bindNativeCarts);
  }
  bindNativeCarts();
  $('.sbcart-footer').hidden = true;
  const loading = document.createElement('li');
  loading.className = 'sbc-loading'; loading.textContent = 'Loading your cart…';
  itemsList.appendChild(loading);
  product(addonHandle).then(p => { addonProduct = p; updateAddon(); }).catch(() => {
    $('.sbcart-public-addons').hidden = true;
  });
  stylesReady.then(() => {
    document.documentElement.classList.add('sb-custom-cart-ready');
    if (!prefersUpcart()) {
      refresh().catch(() => {});
      if (location.pathname.replace(/\/$/, '') === config.cartUrl.replace(/\/$/, '')) routeOpen();
    }
  }).catch(() => {
    // Leave Shopify's native cart available if the replacement stylesheet fails.
    window.SoleBraceCart = null;
  });
})();
